import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardActionArea,
  CardContent,
  Paper,
  Typography,
} from '@mui/material';

import useInsuranceInfo from '../../../hooks/useInsuranceInfo';
import {
  useSelectCustomer,
  DA_DIREKT_POLICY_CATEGORIES,
  RISK_CARRIER_AMTRUST,
  SPECIAL_TARIFF_POLICY_CATEGORIES,
} from '../../../shared';
import { myProfileLinkItems, helpLinkItems } from '../navMenuConfig';
import {
  getInsuredPersonName,
  getNameAccordingToInsuranceCategory,
} from '../../../utils/customerUtils';
import { getMenuLinksConfig } from '../navMenuUtils';

import CaretDownIcon from '../../../icons/CaretDownIcon';

import NavMenuMobileUserInfo from './NavMenuMobileUserInfo';
import NavMenuSignoutButton from '../NavMenuSignoutButton';
import NavMenuMobileSubMenuItem from './NavMenuMobileSubMenuItem';
import NavMenuSelectCustomer from '../NavMenuSelectCustomer';

import {
  StyledNavMenuMobileContainer,
  StyledNavMenuMobileOverlay,
  StyledNavMenuMobileMenuItem,
  StyledVerticalContent,
  StyledSignoutButtonContainer,
  StyledSeparator,
  StyledNavMenuAccordionSummary,
  StyledNavMenuMobileItemLink,
} from './NavMenuMobile.styled';

function NavMenuMobile(props) {
  const { showNavMenu, customer, customers, toggleNavMenu, customerSelect } = props;

  const tabIndex = showNavMenu ? 0 : -1;
  const location = useLocation();
  const history = useHistory();
  const intl = useIntl();

  const urlValues = queryString.parse(location.search);
  const showInner = !useSelectCustomer(location.pathname) && !urlValues.auth;

  const isOnlyCustomer = customers?.length === 1;
  const inactiveContractText = intl.formatMessage({ id: 'contract.placeholder.inactive' });

  const { type: insuranceType, isPatolo } = useInsuranceInfo();

  const customerInsuranceCategory = customer?.insuranceCategory;
  const customerPolicyCategory = customer?.contract?.policyCategory;

  const isAmtrustCustomer = customer?.contract?.riskCarrier === RISK_CARRIER_AMTRUST;

  const isDaDirektCustomer =
    Number.isInteger(customerPolicyCategory) &&
    DA_DIREKT_POLICY_CATEGORIES.includes(customerPolicyCategory);

  const isSpecialTariffCustomer =
    Number.isInteger(customerPolicyCategory) &&
    SPECIAL_TARIFF_POLICY_CATEGORIES.includes(customerPolicyCategory);

  const gotoAndToggleNav = (to) => {
    if (!to) return;

    history.push(to);
    toggleNavMenu();
  };

  const handleExternalLinkClick = (ev, link) => {
    if (ev) ev.preventDefault();

    if (!link) return;

    window.open(link, '_blank');
  };

  const insuredPersonName = getInsuredPersonName(customer);
  const customerDescription =
    customer?.endedContract === 'true' ? inactiveContractText : customer?.policyName || '';

  const menuLinksConfig = getMenuLinksConfig(
    insuranceType,
    customerInsuranceCategory,
    isDaDirektCustomer,
    isAmtrustCustomer,
    isSpecialTariffCustomer,
    isPatolo
  );

  return (
    <StyledNavMenuMobileOverlay open={showNavMenu}>
      {showInner && (
        <StyledNavMenuMobileContainer>
          <StyledNavMenuMobileMenuItem>
            {isOnlyCustomer ? (
              <Paper elevation={0}>
                <NavMenuMobileUserInfo name={insuredPersonName} policyName={customerDescription} />
              </Paper>
            ) : (
              <Accordion elevation={0}>
                <AccordionSummary
                  expandIcon={<CaretDownIcon />}
                  aria-controls="select_customer_content"
                  id="select_customer_summary"
                  tabIndex={tabIndex}
                  aria-label={`${insuredPersonName} ${customerDescription}`}
                >
                  <NavMenuMobileUserInfo
                    name={insuredPersonName}
                    policyName={customerDescription}
                  />
                </AccordionSummary>

                <AccordionDetails>
                  <StyledVerticalContent>
                    {customers?.map((customerItem) => {
                      const { key, policyName, insuranceCategory, endedContract } = customerItem;
                      const contractEnded = endedContract === 'true';
                      const policyDisplayName = contractEnded ? inactiveContractText : policyName;
                      const isCurrentCustomer = customer?.key === key;
                      const customerName = getNameAccordingToInsuranceCategory(customerItem);

                      return (
                        <Card elevation={2} key={key}>
                          <CardActionArea
                            onClick={() =>
                              !isCurrentCustomer ? customerSelect(customerItem?.uuid) : null
                            }
                            disabled={contractEnded}
                            tabIndex={tabIndex}
                            aria-label={`${customerName} ${policyDisplayName} ${isCurrentCustomer ? 'selected' : ''}`}
                          >
                            <CardContent>
                              <NavMenuSelectCustomer
                                policyName={policyDisplayName}
                                customerName={customerName}
                                insuranceCategory={insuranceCategory}
                                isCurrentCustomer={isCurrentCustomer}
                                contractEnded={contractEnded}
                              />
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </StyledVerticalContent>
                </AccordionDetails>
              </Accordion>
            )}
          </StyledNavMenuMobileMenuItem>

          <StyledSeparator />

          <StyledNavMenuMobileMenuItem>
            <Accordion elevation={0}>
              <StyledNavMenuAccordionSummary
                expandIcon={<CaretDownIcon />}
                aria-controls="my_profile_content"
                id="my_profile_summary"
                tabIndex={tabIndex}
              >
                <Typography variant="h3">
                  <FormattedMessage id="menu.header.item.my_profile" />
                </Typography>
              </StyledNavMenuAccordionSummary>

              <AccordionDetails>
                <StyledVerticalContent>
                  <NavMenuMobileSubMenuItem
                    menuItems={myProfileLinkItems(intl)}
                    tabIndex={tabIndex}
                    toggleNavMenu={toggleNavMenu}
                  />
                </StyledVerticalContent>
              </AccordionDetails>
            </Accordion>
          </StyledNavMenuMobileMenuItem>

          {menuLinksConfig.map((linkItem) => (
            <StyledNavMenuMobileMenuItem key={linkItem.path || linkItem.link}>
              {linkItem?.externalUrl ? ( // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <StyledNavMenuMobileItemLink
                  variant="h3"
                  component="button"
                  tabIndex={tabIndex}
                  onClick={(event) => handleExternalLinkClick(event, linkItem.link)}
                >
                  {linkItem?.title}
                </StyledNavMenuMobileItemLink>
              ) : (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <StyledNavMenuMobileItemLink
                  variant="h3"
                  component="button"
                  tabIndex={tabIndex}
                  onClick={() => gotoAndToggleNav(linkItem.path)}
                >
                  {linkItem.title}
                </StyledNavMenuMobileItemLink>
              )}
            </StyledNavMenuMobileMenuItem>
          ))}

          <StyledNavMenuMobileMenuItem>
            <Accordion elevation={0}>
              <StyledNavMenuAccordionSummary
                expandIcon={<CaretDownIcon />}
                aria-controls="help_content"
                id="help_summary"
                tabIndex={tabIndex}
              >
                <Typography variant="h3">
                  <FormattedMessage id="menu.header.item.help" />
                </Typography>
              </StyledNavMenuAccordionSummary>

              <AccordionDetails>
                <StyledVerticalContent>
                  <NavMenuMobileSubMenuItem
                    menuItems={helpLinkItems(intl, insuranceType, isPatolo)}
                    tabIndex={tabIndex}
                    toggleNavMenu={toggleNavMenu}
                  />
                </StyledVerticalContent>
              </AccordionDetails>
            </Accordion>
          </StyledNavMenuMobileMenuItem>

          <StyledSeparator />

          <StyledSignoutButtonContainer>
            <NavMenuSignoutButton tabIndex={tabIndex} />
          </StyledSignoutButtonContainer>
        </StyledNavMenuMobileContainer>
      )}
    </StyledNavMenuMobileOverlay>
  );
}

export default NavMenuMobile;

NavMenuMobile.defaultProps = {
  showNavMenu: false,
  customers: [],
  customer: null,
};

NavMenuMobile.propTypes = {
  showNavMenu: PropTypes.bool,
  toggleNavMenu: PropTypes.func,
  customerSelect: PropTypes.func,
  customers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      policyName: PropTypes.string,
      insuranceCategory: PropTypes.string,
      endedContract: PropTypes.string,
    })
  ),
  customer: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    endedContract: PropTypes.string,
    policyName: PropTypes.string,
    contract: PropTypes.shape({
      policyCategory: PropTypes.number,
      riskCarrier: PropTypes.string,
    }),
  }),
};
